import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Tooltip from 'app/components/Tooltip';
import { ChevronUpDownIcon } from 'app/components/shared/icons';
import CheckIcon from 'app/components/shared/icons/check-icon';
import { Field } from 'formik';
import useOnClickOutside from 'hooks/click-outside';
import { companyNames, matchesCompanyName } from 'utils/company-options';

import { ProfileEditInputProps } from '..';
import {
  ProfileEditInputSelectAddOption,
  ProfileEditInputSelectContainer,
  ProfileEditInputSelectError,
  ProfileEditInputSelectErrorContainer,
  ProfileEditInputSelectIconContainer,
  ProfileEditInputSelectLimit,
  ProfileEditInputSelectOptionContainer,
  ProfileEditInputSelectOptionContainerText,
  ProfileEditInputSelectOptionContent,
  ProfileEditInputSelectOptionMeta,
  ProfileEditInputSelectOptionType,
  ProfileEditInputSelectOptionsWrapper,
  ProfileEditInputSelectWrapper,
} from './styled';

const defaultSettings = {
  allowMultiple: false,
  allowCreate: false,
  appendDataOnCreate: {},
  disabledOptions: [],
  disabledOptionsMessage: '',
  disabledOptionsTooltip: '',
  showOptionType: false,
};

export default function ProfileEditInputSelect(props: ProfileEditInputProps) {
  const {
    name,
    values,
    textLimit,
    options,
    settings = defaultSettings,
    onOptionPicked,
    onNewOptionPicked,
    onOptionsChanged,
    onCurrenOptionDeleted,
    keepValueOnChange,
    overrideOnOptionPicked,
    otherNames = [],
  } = props;
  const [showOptions, setShowOptions] = useState<boolean>(false);
  const [optionsCreated, setOptionsCreated] = useState<any[]>([]);
  const [currentFilter, setCurrentFilter] = useState<string>('');
  const [currentOption, setCurrentOption] = useState<any>(null);
  const inputWrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useOnClickOutside(inputWrapperRef, () => setShowOptions(false));

  useEffect(() => {
    if (onOptionsChanged) {
      onOptionsChanged([
        ...options.filter((opt) => values[name]?.includes(opt.value)),
        ...optionsCreated,
      ]);
      setOptionsCreated([]);
    }
  }, [values[name], options]);

  useEffect(() => {
    if (
      !currentOption &&
      currentFilter.length === 0 &&
      document.activeElement === inputRef.current
    ) {
      setShowOptions(true);
    }
  }, [currentFilter, currentOption]);

  const filterData = (option: { value: any; label: string }) => {
    return companyNames(option).some((name) => name.toLowerCase().trim().includes(currentFilter.toLowerCase().trim()));
  };

  const addCreateOption = () => {
    if (
      settings.allowCreate &&
      currentFilter &&
      !options.some((opt) => matchesCompanyName(opt, currentFilter)) &&
      currentFilter.trim().length > 0
    ) {
      const newValue = { value: currentFilter, label: currentFilter, isCreated: true };
      return [newValue];
    }
    return [];
  };

  const handleChange = (e: React.FormEvent<HTMLInputElement>) => {
    if (e.currentTarget.value === '' && currentOption) {
      setCurrentOption(null);
      onCurrenOptionDeleted?.();
    }
    setCurrentFilter(e.currentTarget.value);
  };

  const resetInput = () => {
    if (!keepValueOnChange) {
      setCurrentFilter('');
    }
    setShowOptions(false);
  };

  const handleOptionPicked = (option) => {
    setCurrentFilter('');

    if (overrideOnOptionPicked) {
      setCurrentOption(option);
      const result = overrideOnOptionPicked(option);
      setCurrentOption(null);
      setShowOptions(false);
      return result;
    }
    if (option.isCreated && onNewOptionPicked) {
      setCurrentOption(option);
      onNewOptionPicked({ ...option, ...(settings.appendDataOnCreate || {}) });
      setCurrentOption(null);
      setShowOptions(false);
      return;
    }

    if (onOptionPicked) {
      onOptionPicked(option);
    }

    const index = values[name].findIndex((opt) => opt === option.value);

    if (index !== -1) {
      const newOptions = values[name].filter((opt) => opt !== option.value);
      if (option.isCreated) {
        setOptionsCreated(optionsCreated.filter((opt) => opt.value !== option.value));
      }
      props.setFieldValue(name, newOptions);
    } else {
      if (option.isCreated) {
        setOptionsCreated([...optionsCreated, option]);
      }
      props.setFieldValue(name, [...values[name], option.value]);
    }

    props.setFieldTouched(name, true);

    resetInput();
  };

  const handleOnClick = () => {
    if (!currentOption) {
      setShowOptions(true);
    }
  };

  const onOptionClicked = (option: { value: any; label: string; isCreated?: boolean }) => {
    const finalOption = { ...option, timestamp: new Date().getTime() };
    if (option.isCreated) {
      handleOptionPicked({
        ...finalOption,
        uuid: finalOption.value,
        ...(settings.appendDataOnCreate || {}),
      });
    } else {
      handleOptionPicked(finalOption);
    }
  };

  const isOptionDisabled = useCallback(
    (option) => {
      if (!settings.disabledOptions) return false;
      if (settings.disabledOptions.length === 0) return false;

      return settings.disabledOptions.some((opt) => opt.label === option.label);
    },
    [settings.disabledOptions]
  );

  const isOptionPicked = useCallback(
    (option) => {
      if (otherNames.length > 0) {
        return (
          otherNames.some((name) => props.values[name]?.includes(option.value)) ||
          props.values[name]?.includes(option.value)
        );
      }

      return props.values[name]?.includes(option.value);
    },
    [props.values[name], otherNames]
  );

  return (
    <ProfileEditInputSelectWrapper ref={inputWrapperRef}>
      <Field type="hidden" />
      <ProfileEditInputSelectContainer
        ref={inputRef}
        value={currentFilter}
        onChange={handleChange}
        placeholder={props.placeholder}
        isValid={!props.getFieldMeta(name).error}
        onClick={handleOnClick}
        disabled={props.disabled}
        className={`select-input-${name}`}
      />
      {showOptions && (
        <ProfileEditInputSelectOptionsWrapper>
          {[...addCreateOption(), ...options].filter(filterData).map((option, index) => {
            const isDisabled = isOptionDisabled(option);

            if (isDisabled) {
              const disabledItem = settings.disabledOptions.find(
                (opt) => opt.label === option.label
              );
              return (
                <Tooltip
                  text={
                    typeof settings.disabledOptionsTooltip === 'function'
                      ? settings.disabledOptionsTooltip(disabledItem)
                      : settings.disabledOptionsTooltip
                  }
                >
                  <ProfileEditInputSelectOptionContainer
                    key={option.value}
                    onClick={() => {}}
                    isDisabled={isDisabled}
                  >
                    <ProfileEditInputSelectOption
                      disabled={isDisabled}
                      settings={settings}
                      option={disabledItem}
                      currentFilter={currentFilter}
                      picked={isOptionPicked(option)}
                    />
                  </ProfileEditInputSelectOptionContainer>
                </Tooltip>
              );
            }
            return (
              <ProfileEditInputSelectOptionContainer
                key={option.value}
                onClick={() => onOptionClicked(option)}
                isDisabled={isDisabled}
              >
                <ProfileEditInputSelectOption
                  disabled={isDisabled}
                  settings={settings}
                  option={option}
                  currentFilter={currentFilter}
                  picked={isOptionPicked(option)}
                />
              </ProfileEditInputSelectOptionContainer>
            );
          })}
        </ProfileEditInputSelectOptionsWrapper>
      )}
      {textLimit && !props.getFieldMeta(name).error ? (
        <ProfileEditInputSelectLimit>
          {values[name]?.length}/{textLimit}
        </ProfileEditInputSelectLimit>
      ) : null}
      {props.getFieldMeta(name).error ? (
        <ProfileEditInputSelectErrorContainer>
          <ProfileEditInputSelectError>
            {props.getFieldMeta(name).error}
          </ProfileEditInputSelectError>
        </ProfileEditInputSelectErrorContainer>
      ) : null}
      <ProfileEditInputSelectIconContainer>
        <ChevronUpDownIcon width={24} height={24} fill={'#000000'} />
      </ProfileEditInputSelectIconContainer>
    </ProfileEditInputSelectWrapper>
  );
}

function ProfileEditInputSelectOption({
  option,
  currentFilter,
  picked,
  settings,
  disabled,
}: {
  option: {
    value: any;
    label: string;
    isCreated?: boolean;
    certification?: boolean;
    software?: boolean;
  };
  currentFilter: string;
  picked: boolean;
  settings: {
    disabledOptions?: any[];
    disabledOptionsMessage?: string | ((option: any) => string);
    disabledOptionsTooltip?: string | ((option: any) => string);
    showOptionType?: boolean;
  };
  disabled?: boolean;
}) {
  const normalizedFilter = currentFilter.trim();
  const lowerLabel = option.label.toLowerCase();
  const lowerFilter = normalizedFilter.toLowerCase();
  const resultIndex = normalizedFilter.length > 0 ? lowerLabel.indexOf(lowerFilter) : -1;
  const hasHighlight = normalizedFilter.length > 0 && resultIndex >= 0;

  const firstResultPart = hasHighlight ? option.label.substring(0, resultIndex) : option.label;
  const resultPart = hasHighlight
    ? option.label.substring(resultIndex, resultIndex + normalizedFilter.length)
    : '';
  const lastResultPart = hasHighlight
    ? option.label.substring(resultIndex + normalizedFilter.length)
    : '';

  const typeLabel = option.certification ? 'Certification' : option.software ? 'Software' : 'Skill';

  const shouldShowType = settings.showOptionType && !(option.isCreated && !picked);
  const hasMetaContent = shouldShowType || picked;

  return (
    <>
      <ProfileEditInputSelectOptionContent>
        {hasHighlight ? (
          <>
            <ProfileEditInputSelectOptionContainerText>
              {firstResultPart}
            </ProfileEditInputSelectOptionContainerText>
            <ProfileEditInputSelectOptionContainerText
              highlighted
              isCreated={option.isCreated && !picked}
            >
              {resultPart}
            </ProfileEditInputSelectOptionContainerText>
            <ProfileEditInputSelectOptionContainerText>
              {lastResultPart}
            </ProfileEditInputSelectOptionContainerText>
          </>
        ) : (
          <ProfileEditInputSelectOptionContainerText>
            {option.label}
          </ProfileEditInputSelectOptionContainerText>
        )}
      </ProfileEditInputSelectOptionContent>
      {hasMetaContent ? (
        picked ? (
          <ProfileEditInputSelectOptionMeta>
            <ProfileEditInputSelectOptionType>ADDED</ProfileEditInputSelectOptionType>
          </ProfileEditInputSelectOptionMeta>
        ) : (
          <ProfileEditInputSelectOptionMeta>
            {shouldShowType ? (
              <ProfileEditInputSelectOptionType>{typeLabel}</ProfileEditInputSelectOptionType>
            ) : null}
          </ProfileEditInputSelectOptionMeta>
        )
      ) : null}
      {option.isCreated && !picked ? (
        <ProfileEditInputSelectAddOption>
          {disabled
            ? typeof settings.disabledOptionsMessage === 'function'
              ? settings.disabledOptionsMessage(option)
              : settings.disabledOptionsMessage
            : `+ADD`}
        </ProfileEditInputSelectAddOption>
      ) : null}
    </>
  );
}
